import { z } from "zod";
import type { CheerioAPI } from "cheerio";
import type { Url } from "../../data-model/primitives.js";
import { unsafeBrandAdapterId, unsafeBrandChapterIndex } from "../../data-model/primitives.js";
import type { NovelMetadata, NovelStatus, ChapterRef, Chapter } from "../../data-model/novel.js";
import { ParseError } from "../../core/errors.js";
import { loadHtml, normalizeText, wordCount } from "../../utils/html.js";
import type { Logger } from "../../utils/logger.js";
import { refsFromAnchors } from "../shared/anchors.js";
import { extractCoverUrl } from "../shared/cover.js";
import { assertContentNonEmpty } from "../shared/content.js";
import { buildNovelMetadata } from "../shared/header.js";
import { WIKICV_SELECTORS as S } from "./selectors.js";

const SOURCE_SITE = unsafeBrandAdapterId("wikicv");

export function parseNovelPage($: CheerioAPI, sourceUrl: Url): NovelMetadata {
  const title = $(S.metadata.title).first().text().trim();
  if (!title) throw new ParseError("Missing title on novel page", { url: sourceUrl });

  const author = $(S.metadata.author).first().text().trim();

  const descRaw = $(S.metadata.description).first().text().trim();
  // Strip leading "Văn án" header paragraph (appears as first child <p>Văn án</p>)
  // and any surrounding whitespace.
  const description = descRaw.replace(/^V[aă]n\s*án\s*/i, "").trim();

  const coverUrl = extractCoverUrl($, S.metadata.cover, sourceUrl);

  const descBlock = $(S.metadata.genresContainer).first().text();
  const genres = parseGenres(descBlock);

  const statusText = $(S.metadata.statusAnchor).first().text().trim();
  const status: NovelStatus =
    /^Hoàn\s*thành/i.test(statusText)
      ? "completed"
      : /^Đang\s*ra/i.test(statusText)
        ? "ongoing"
        : "unknown";

  return buildNovelMetadata(
    {
      title,
      author,
      description,
      genres,
      ...(coverUrl !== undefined ? { coverUrl } : {}),
    },
    {
      sourceUrl,
      sourceSite: SOURCE_SITE,
      status,
      totalChapters: extractTotalChapters($),
    },
  );
}

// Read the "Mới nhất:" anchor: its slug carries the latest chapter index, and
// because wikicv chapter indices are dense and 1-based, the latest index is
// the source TOC size. Falls back to the visible "Chương N" text when the
// slug shape changes. Returns undefined if neither yields a positive integer.
//
// RISK (unverified): for an ongoing novel with deleted/renumbered chapters, or
// when "Mới nhất" points at a side-story (/chuong-100-phien-ngoai-...), this can
// mis-report totalChapters with no detection path. Cross-check against a
// browser-tier TOC walk at survey time — see
// docs/surveys/2026-06-20-review-deferred-verification.md.
const CHUONG_SLUG_REGEX = /\/chuong-(\d+)-/i;
const CHUONG_TEXT_REGEX = /Chương\s*(\d+)/i;

function extractTotalChapters($: CheerioAPI): number | undefined {
  let best: number | undefined;
  $(S.metadata.latestChapterAnchor).each((_, el) => {
    const a = $(el);
    const href = a.attr("href") ?? "";
    const text = a.text();
    const m = CHUONG_SLUG_REGEX.exec(href) ?? CHUONG_TEXT_REGEX.exec(text);
    if (!m?.[1]) return;
    const n = Number.parseInt(m[1], 10);
    if (Number.isFinite(n) && n > 0 && (best === undefined || n > best)) best = n;
  });
  return best;
}

function parseGenres(block: string): readonly string[] {
  const m = /Thể\s*loại\s*:\s*([\s\S]+?)(?:\n\s*\n|$)/i.exec(block);
  if (!m?.[1]) return [];
  return m[1]
    .split(",")
    .map((s) => s.replace(/\s+/g, " ").trim())
    // Drop description spillover: a real genre is short. When `.book-desc`
    // renders on one line the lazy capture runs into the synopsis.
    .filter((s) => s.length > 0 && s.length <= 50 && s.toLowerCase() !== "thể loại");
}

const PaginationProbeSchema = z.object({
  lastStart: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});

/** Parse the wikicv pagination probe payload. Returns null on any bad input. */
export function parsePaginationProbe(
  raw: unknown,
): { lastStart: number; pageSize: number } | null {
  if (typeof raw !== "string") return null;
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }
  const parsed = PaginationProbeSchema.safeParse(json);
  return parsed.success
    ? { lastStart: parsed.data.lastStart, pageSize: parsed.data.pageSize }
    : null;
}

export function parseChapterPage($: CheerioAPI, ref: ChapterRef): Chapter {
  // The three p.book-title blocks (novel title, chapter label, author line)
  // live in #bookContent — a sibling wrapper around #bookContentBody.
  // Grab the chapter title from the second p.book-title before touching the DOM.
  const titleEl = $(S.chapter.titleBlock).eq(1);
  const title = titleEl.text().trim() || ref.title;

  const body = $(S.chapter.bodyRoot).first();
  if (body.length === 0) {
    throw new ParseError(`Chapter body not found at ${ref.url}`, { url: ref.url });
  }

  // Strip residual UI / help / ads / comment blocks inside the body (defensive).
  body
    .find(
      ".comment-title, .book-comment, [class*='comment'], [class*='ads'], .chapter-nav, .tooltip-link",
    )
    .remove();

  const content = normalizeText(body.html() ?? "");
  assertContentNonEmpty(content, ref.url);

  return {
    index: ref.index,
    url: ref.url,
    title,
    content,
    wordCount: wordCount(content),
    fetchedAt: new Date(),
  };
}

/**
 * Parse the HTML response from /book/index?bookId=... — a fragment containing
 * <li class="chapter-name"><a class="truncate" href="...">title</a></li> items.
 *
 * `startIndex` shifts the global index for windows beyond the first, so paged
 * walks (start=0, 501, 1002, …) yield a continuous sequence. `baseUrl` is the
 * URL of the page that produced the fragment — relative anchors resolve
 * against it so a `www.` host or a mirror domain in the user input is
 * preserved instead of collapsing to a hardcoded origin.
 */
export function parseBookIndexHtml(
  html: string,
  baseUrl: Url,
  startIndex = 0,
  opts: { logger?: Logger } = {},
): readonly ChapterRef[] {
  const $ = loadHtml(html);
  const refs = refsFromAnchors($, {
    selector: S.toc.chapterList,
    startIndex: unsafeBrandChapterIndex(startIndex),
    baseUrl,
    preferTitleAttr: false,
  });
  if (refs.length === 0) {
    opts.logger?.warn({ url: baseUrl, startIndex }, "wikicv: empty book/index window — ending walk");
  }
  return refs;
}
